import { ALL_CHECKBOX_DEFINITIONS } from '~/constants/checkboxDefinitions';

export default {
  data() {
    return {
      /**
       * @type {object.<string, boolean>} checkboxStates - Объект, хранящий текущее состояние каждого чекбокса по его имени.
       * Например: { privacy: true, rf: false, agreement: true }
       */
      checkboxStates: {},

      /**
       * @type {boolean} validationAttempted - Флаг, указывающий, была ли предпринята попытка валидации формы (и чекбоксов).
       */
      validationAttempted: false,
    };
  },
  /**
   * Хук жизненного цикла: вызывается при создании экземпляра компонента.
   * Инициализирует начальные состояния чекбоксов.
   */
  created() {
    this.initializeCheckboxStates();
  },
  computed: {
    /**
     * @type {string[]} invalidCheckboxes - Массив имен ОБЯЗАТЕЛЬНЫХ чекбоксов, которые не были отмечены.
     * Пересчитывается автоматически при изменении checkboxStates или validationAttempted.
     * Возвращает пустой массив, если validationAttempted === false.
     * Обязательность определяется по флагу `required` в ALL_CHECKBOX_DEFINITIONS.
     */
    invalidCheckboxes() {
      if (!this.validationAttempted) {
        return [];
      }

      const uncheckedRequiredNames = [];
      ALL_CHECKBOX_DEFINITIONS.forEach(def => {
        if (def.required) {
          if (this.checkboxStates?.[def.name] !== true) {
            uncheckedRequiredNames.push(def.name);
          }
        }
      });

      return uncheckedRequiredNames;
    }
  },
  methods: {
    /**
     * Инициализирует или сбрасывает состояния всех известных чекбоксов к их начальным значениям,
     * указанным в ALL_CHECKBOX_DEFINITIONS. Сбрасывает флаги валидации.
     */
    initializeCheckboxStates() {
      const initialStates = {};
      ALL_CHECKBOX_DEFINITIONS.forEach(def => {
        initialStates[def.name] = def.initial !== undefined ? def.initial : true;
      });
      this.checkboxStates = initialStates;
      this.validationAttempted = false;
    },

    /**
     * Обработчик изменения состояния одного чекбокса.
     * Обновляет состояние конкретного чекбокса в checkboxStates.
     * Поскольку checkboxStates является зависимостью computed свойства invalidCheckboxes,
     * изменение здесь вызовет пересчет invalidCheckboxes, если validationAttempted === true.
     * @param {object} payload - Объект с данными изменения.
     * @param {string} payload.name - Имя изменившегося чекбокса.
     * @param {boolean} payload.value - Новое состояние чекбокса (true/false).
     */
    handleCheckboxChange({ name, value }) {
      this.$set(this.checkboxStates, name, value);
    },

    /**
     * Запускает валидацию ОБЯЗАТЕЛЬНЫХ чекбосов (определенных в константах с флагом `required: true`).
     * Устанавливает флаг validationAttempted в true.
     * Это запускает реактивный пересчет invalidCheckboxes.
     * @returns {boolean} - Возвращает true, если все обязательные чекбосы отмечены, иначе false.
     */
    validateCheckboxes() {
      this.validationAttempted = true;
      return this.invalidCheckboxes.length === 0;
    },

    /**
     * Сбрасывает состояния всех чекбоксов к их начальным значениям
     * и сбрасывает флаги валидации.
     * Используется, например, после успешной отправки формы или при закрытии модалки.
     */
    resetCheckboxes() {
      this.initializeCheckboxStates();
    },

    /**
     * Возвращает массив всех определенных чекбоксов.
     * @returns {CheckboxDefinition[]}
     */
    getAllCheckboxDefinitions() {
      return ALL_CHECKBOX_DEFINITIONS;
    },

    /**
     * Возвращает текущее состояние конкретного чекбокса по его имени.
     * @param {string} name - Имя чекбокса.
     * @returns {boolean | undefined} - Текущее состояние (true/false) или undefined, если чекбокс с таким именем не определен.
     */
    getCheckboxState(name) {
      return this.checkboxStates?.[name];
    },

    /**
     * Возвращает объект с текущими состояниями всех известных чекбоксов.
     * @returns {object.<string, boolean>} - Объект, где ключи - имена чекбоксов, значения - их состояния.
     */
    getAllCheckboxStates() {
      return this.checkboxStates;
    }
  }
};
